import { Dialog } from '@mui/material';
import * as React from 'react';

export interface OpenModalOptions {
  onClose?: () => void;
  maxWidth?: number;
  width?: string;
}

export interface ModalContextType {
  openModal: (content: React.ReactNode, options?: OpenModalOptions) => void;
  closeModal: () => void;
}

export const ModalContext = React.createContext<ModalContextType | undefined>(undefined);

const DEFAULT_MAX_WIDTH = 600;

export const ModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [modalContent, setModalContent] = React.useState<React.ReactNode>(null);
  const [maxWidth, setMaxWidth] = React.useState(DEFAULT_MAX_WIDTH);
  const [width, setWidth] = React.useState<string | undefined>(undefined);
  const onCloseCallbackRef = React.useRef<(() => void) | undefined>(undefined);

  const openModal = React.useCallback((content: React.ReactNode, options?: OpenModalOptions) => {
    onCloseCallbackRef.current = options?.onClose;
    setMaxWidth(options?.maxWidth ?? DEFAULT_MAX_WIDTH);
    setWidth(options?.width);
    setModalContent(content);
    setIsOpen(true);
  }, []);

  const closeModal = React.useCallback(() => {
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
      onCloseCallbackRef.current = undefined;
    }
    setIsOpen(false);
  }, []);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setModalContent(null);
      setMaxWidth(DEFAULT_MAX_WIDTH);
      setWidth(undefined);
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={closeModal}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 4 },
            width: { xs: '100%', sm: width ?? 'auto' },
            maxWidth: { xs: '100%', sm: maxWidth },
            maxHeight: { xs: '100svh', sm: 'calc(100svh - 64px)' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
        slotProps={{ transition: { onExited: handleTransitionEnd } }}
      >
        {modalContent}
      </Dialog>
    </ModalContext.Provider>
  );
};

export default ModalProvider;
